import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle, KeyRound } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // request, token, reset, success
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/password-reset/request`, { email });
      toast.success('If the email exists, a reset link has been generated');
      setStep('token');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request reset');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerifyToken = async (e) => {
    e.preventDefault();
    if (!token.trim()) {
      toast.error('Please enter the reset token');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/password-reset/verify`, { token });
      if (res.data.valid) {
        toast.success('Token verified');
        setStep('reset');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired token');
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/password-reset/reset`, {
        token,
        new_password: newPassword
      });
      toast.success('Password reset successful');
      setStep('success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <KeyRound className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">
            {step === 'success' ? 'Password Reset Complete' : 'Reset Password'}
          </CardTitle>
          <CardDescription>
            {step === 'request' && 'Enter your email to receive a reset token'}
            {step === 'token' && 'Enter the reset token you received'}
            {step === 'reset' && 'Enter your new password'}
            {step === 'success' && 'You can now login with your new password'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {step === 'request' && (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              
              <Button type="submit" className="w-full" disabled={loading} data-testid="request-reset-btn">
                {loading ? 'Sending...' : 'Request Reset Token'}
              </Button>
              
              <div className="text-center">
                <Link to="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                  <ArrowLeft className="h-3 w-3" /> Back to Login
                </Link>
              </div>
            </form>
          )}
          
          {step === 'token' && (
            <form onSubmit={handleVerifyToken} className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800 mb-4">
                <p>A reset token has been generated. In demo mode, contact your administrator to get the token, or check the admin panel.</p>
              </div>
              
              <div>
                <Label htmlFor="token">Reset Token</Label>
                <Input
                  id="token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Enter reset token"
                  data-testid="reset-token-input"
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={loading} data-testid="verify-token-btn">
                {loading ? 'Verifying...' : 'Verify Token'}
              </Button>
              
              <Button type="button" variant="outline" className="w-full" onClick={() => setStep('request')}>
                Request New Token
              </Button>
            </form>
          )}
          
          {step === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  data-testid="new-password-input"
                />
              </div>
              
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
          
          {step === 'success' && (
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-muted-foreground">Your password has been reset successfully.</p>
              <Button onClick={() => navigate('/login')} className="w-full" data-testid="go-to-login-btn">
                Go to Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
